import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { Card } from '../game/data/cards/types';
import { allCards } from '../game/data/cards';
import { getTribeDefinition } from '../game/data/tribes';
import { getTribeVisualSpec } from './procedural/tribeVisualSpec';
import { getAccessoryComponent } from './procedural/accessoryRegistry';
import { resolveFrameVariant } from './procedural/frames';
import { VirgenMotif } from './procedural/VirgenMotif';
import { colors, EDGES } from '../theme/tokens';
import { useCardDetailStore } from '../stores/cardDetail';
import { useUiSelectionStore } from '../stores/uiSelection';
import { CardHoverPreview, type HoverAnchor } from './CardHoverPreview';

// Tiny hex→rgba helper so portrait gradients can reference theme tokens
// instead of duplicating the underlying channel values inline.
function rgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Procedural unit card — used in Draft (R4) and reused for Shop / Hand (R5).
// Same identity rules as HeroCard: data only, no portrait art assets.
//
// Visual structure (mobile-first):
//   ┌─────────────┐
//   │ T  [FACTION]│   tier chip + faction watermark
//   │             │
//   │   AA        │   2-letter initials, tribe color, faction font
//   │             │
//   │ A         H │   stats: ATK (aerosol red) / HP (glowstick green)
//   ├─────────────┤
//   │ Card Name   │
//   │ tribe · text│
//   └─────────────┘
//
// `starter` flag adds a sodium-glow ring (R4 highlights hero-specific starters).
// `selected` flag adds a thicker sodium ring (player picked it).

/**
 * Content density, not box size.
 *
 *   'sm'      — icon mode. Square, no foot, no accessory, minimal badges.
 *               Used where a card is too small to read text (compact
 *               landscape phones). Name still reachable via inspect.
 *   'md'      — full card: portrait + foot with name, tribe, rules text.
 *   'lg'      — same anatomy as 'md' with a taller foot and larger type.
 *               For portrait phones, where the rails render cards ~170-220px
 *               wide. The foot's type used to key off VIEWPORT breakpoints,
 *               so a 220px-wide card on a 390px screen still drew its rules
 *               text at 9px — big card, unreadable card. This tier sizes the
 *               text to the card instead.
 *   'preview' — the enlarged, non-interactive rendition. Two callers:
 *               CardHoverPreview (the desktop hover panel) and CardDetail,
 *               as the art panel for cards that have no art asset yet.
 *
 * The *box* is owned by the parent — CardTile fills its container and
 * clamps with max-h-full. This is deliberate: sizing used to be driven by
 * viewport-height media queries baked into the card, which meant no caller
 * could place a card without fighting them.
 */
export type CardSize = 'sm' | 'md' | 'lg' | 'preview';

/** Aspect ratio a given size renders at. Rail wrappers apply this to
    themselves so the box is `height (from the rail) × aspect`, which is
    deterministic — a card that owns both its aspect AND `w-auto` inside a
    flex row has no width to resolve against and collapses to zero. */
export function cardAspectClass(size: CardSize): string {
  return size === 'sm' ? 'aspect-square' : 'aspect-[3/4]';
}

interface CardTileProps {
  card: Card;
  selected?: boolean;
  starter?: boolean;
  onClick?: () => void;
  /** Whether the current player can pay for this card. Unlike `disabled`,
      an unaffordable card stays interactive so it can still be inspected or
      selected. */
  affordable?: boolean;
  /** Non-golden copies currently owned. One or two surfaces triple progress. */
  copiesOwned?: number;
  /** A second tap on an already-selected card takes the primary action. */
  onConfirmTap?: () => void;
  disabled?: boolean;
  size?: CardSize;
  /**
   * Size from height instead of width — the card fills its container's
   * height and derives width from the aspect ratio.
   *
   * This is what rails need. In a horizontal scroller width is the free
   * axis and height is the hard constraint, so width-driven sizing lets
   * cards overflow their zone (the stat badges end up rendering below it).
   */
  fill?: boolean;
}

/** True when the primary pointer actually hovers (mouse/trackpad). Taps on
    touch screens fire synthetic mouse events, so hover-only affordances must
    check this rather than trusting mouseenter. Queried lazily — input
    capability can change (tablet + attached mouse). */
function canHover(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

/** Rest on a card this long before the hover preview opens. Long enough that
    sweeping the cursor across a rail never flashes one, short enough that a
    deliberate hover still feels immediate. */
const HOVER_INTENT_MS = 220;

// Template stats per card id — used to detect buffed-above-base stats so
// they can be highlighted (HS-style "green numbers"). Golden cards
// compare against 2× base (triples double stats). Tokens minted outside
// the card registry (hero-power 1/1s) just miss the map → no highlight.
const baseStatsById = new Map(
  allCards.map((c) => [c.id, { attack: c.attack, health: c.health }]),
);

function hexFromColorInt(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

function getInitials(name: string): string {
  const parts = name.split(/[\s-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const factionFontFor = (f: Card['faction']) =>
  f === 'cholo' ? 'font-cholo' : f === 'emo' ? 'font-emo' : 'font-cholo';

// Solid-ish faction-tinted background for the portrait area. Acts as a
// placeholder until card art ships — cholo cards lean warm orange, emo
// cards lean hot pink, neutral cards stay near-black with a subtle warm
// hotspot so they read as "outside the factions". All values derived
// from the colors token so faction palette changes propagate here.
function factionPortraitBackground(faction: Card['faction']): string {
  if (faction === 'cholo') {
    // Warm orange wash — heavier at top, fading to the dark stucco at
    // the bottom so the ATK/HP badges still read.
    return `linear-gradient(170deg, ${rgba(colors.cholo, 0.62)} 0%, ${rgba(colors.cholo, 0.32)} 55%, ${rgba(colors.void, 0.95)} 100%)`;
  }
  if (faction === 'emo') {
    // Hot pink wash — same shape, different hue.
    return `linear-gradient(170deg, ${rgba(colors.emo, 0.58)} 0%, ${rgba(colors.emo, 0.28)} 55%, ${rgba(colors.void, 0.95)} 100%)`;
  }
  // Neutrals stay grimy — they live outside the factions visually.
  return `radial-gradient(circle at 30% 25%, ${rgba(colors.sodium, 0.08)} 0%, ${colors.void} 75%)`;
}

export function CardTile({
  card,
  selected = false,
  starter = false,
  onClick,
  affordable = true,
  copiesOwned = 0,
  onConfirmTap,
  disabled = false,
  size = 'md',
  fill = false,
}: CardTileProps) {
  const preview = size === 'preview';
  // Icon mode — too small for the foot, tape strip, accessory, or plaque
  // counter. Everything that survives has to read at ~90px.
  const icon = size === 'sm';
  // Portrait rails render cards big enough that the viewport-keyed type
  // scale under-sizes them badly — see the CardSize docs.
  const big = size === 'lg';
  const tribe = card.tribe ? getTribeDefinition(card.tribe) : undefined;
  const tribeColor = tribe ? hexFromColorInt(tribe.color) : '#8a7e6c';
  const initials = getInitials(card.name);
  const factionFont = factionFontFor(card.faction);

  // Procedural rendering layer — tribe-specific signature accessory.
  // Per docs/visual-conventions.md: accessory > body at card-portrait scale.
  // Falls back gracefully (no accessory) for neutral cards or tribes
  // whose primitive hasn't been built yet (see accessoryRegistry).
  const visualSpec = getTribeVisualSpec(card.tribe);
  const Accessory = visualSpec ? getAccessoryComponent(visualSpec.accessoryId) : null;

  // Frame variant — tier-6 mythic gets gold inlay + halo; California foils
  // get a distinctly cleaner outsider look. Default frame is a no-op.
  const frame = resolveFrameVariant(card);
  // The unaffordable badge and the tribe accessory both want the top-right
  // corner of a ~176px tile, and they collided visibly on Plaqueros/Bajitos
  // shop cards. Functional state beats decoration when the tile is crowded:
  // the accessory stands down while the badge is up. Identity is still carried
  // by the initials, the tribe colour, the name and the tribe label in the foot.
  const showAccessory =
    Accessory && visualSpec && frame.showTribeAccessory && affordable;

  // Virgen de Guadalupe motif — sparingly applied via the 'virgen' tag.
  // Renders as a low-opacity halo behind the initials. Cross-faction binding
  // visual per docs/visual-conventions.md (max 2–3 marquee cards per faction).
  const hasVirgenMotif = card.tags?.includes('virgen');

  const isInteractive = !!onClick && !disabled;
  const Tag = isInteractive ? motion.button : motion.div;

  // Displayed stats fold in any pending "this combat only" buff so the
  // player sees what will actually fight. Temp-buffed stats get an ice
  // ring; permanently-buffed-above-base stats get a sodium ring.
  const tempAtk = card.combatBuff?.attack ?? 0;
  const tempHp = card.combatBuff?.health ?? 0;
  const displayAtk = card.attack + tempAtk;
  const displayHp = card.health + tempHp;
  const base = baseStatsById.get(card.id);
  const baseMul = card.isGolden ? 2 : 1;
  const atkBuffed = !!base && card.attack > base.attack * baseMul;
  const hpBuffed = !!base && card.health > base.health * baseMul;
  const atkRing =
    tempAtk !== 0
      ? 'ring-1 ring-iceCrystal'
      : atkBuffed
        ? 'ring-1 ring-sodium'
        : '';
  const hpRing =
    tempHp !== 0
      ? 'ring-1 ring-iceCrystal'
      : hpBuffed
        ? 'ring-1 ring-sodium'
        : '';
  const plaqueStacks = card.plaqueStacks ?? 0;

  // --- Reading a card: hover (desktop) and inspect (everywhere) ---
  //
  // CardTile is the one component every card surface renders through, so
  // wiring both here covers shop, board, hand, draft and discover without
  // touching those screens.
  //
  //   hover   — desktop only, rest on a card: the enlarged preview panel.
  //   inspect — the full lightbox. Desktop right-click (or the context-menu
  //             key); touch taps to select, then uses the ActionBar's Info
  //             verb.
  //
  // Touch never opens the lightbox from here: the browser's own long-press is
  // what fires `contextmenu` on a phone, and honouring it would re-create a
  // hold-gesture conflict with drag. We still preventDefault on touch so the
  // OS text/image callout stays suppressed.
  //
  // HISTORY — a LONG-PRESS TIMER used to be a third entry point, racing
  // dnd-kit's 8px PointerSensor and the rail's pan-x scroll for the same
  // finger-down. It needed movement slop and a one-shot click suppressor so
  // reading a shop card didn't also buy it. That one is gone for good:
  // pointer-hold means exactly one thing now, and that is drag.
  const openDetail = useCardDetailStore((s) => s.open);
  // contextmenu is a MouseEvent and carries no pointerType, so record it
  // from the press that preceded it. Stays null for the context-menu key,
  // which is a keyboard affordance and should inspect.
  const lastPointerType = useRef<string | null>(null);
  // A click normally follows pointerup, including after some drag sequences.
  // Keep gesture distance locally so a dnd-kit activation can never become a
  // confirm tap even if the global dragging flag has already reset by click.
  const confirmGesture = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    dragged: boolean;
  } | null>(null);

  // --- Hover preview ---
  //
  // Two guards, and that is the whole thing. See CardHoverPreview for why the
  // cursor-anchored version needed four and still lost to drag.
  //
  //   1. `canHover()` — a tap on a touchscreen synthesizes mouseenter, so
  //      without this the panel pops on phones where it has no business
  //      existing.
  //   2. `dragging` — a drag re-parents and re-mounts cards under a moving
  //      cursor, firing enter events on cards the player is not hovering.
  //      This flag is authoritative for the whole life of the gesture, unlike
  //      any per-event button-state test.
  //
  // Everything else (scroll, wheel, resize, a press starting anywhere) is
  // handled once, inside the panel, as dismissal.
  const tileRef = useRef<HTMLElement | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<HoverAnchor | null>(null);
  const draggingNow = useUiSelectionStore((s) => s.dragging);
  // An open lightbox is the deeper view of the same card — a preview behind it
  // is noise.
  const detailOpen = useCardDetailStore((s) => s.card !== null);
  const hoverActive = hoverAnchor !== null && !draggingNow && !detailOpen;

  const cancelHoverTimer = useCallback(() => {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  const dismissHover = useCallback(() => {
    cancelHoverTimer();
    setHoverAnchor((prev) => (prev === null ? prev : null));
  }, [cancelHoverTimer]);

  function handleMouseEnter() {
    if (preview || !canHover() || draggingNow) return;
    cancelHoverTimer();
    hoverTimer.current = window.setTimeout(() => {
      hoverTimer.current = null;
      // Measured at open, not at enter: the rail may have settled since, and
      // a stale rect is what puts a panel in the wrong place.
      const rect = tileRef.current?.getBoundingClientRect();
      if (!rect) return;
      setHoverAnchor({
        rect: {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        },
      });
    }, HOVER_INTENT_MS);
  }

  // A drag can start while the panel is already open (press, then move), and
  // the panel's own pointerdown dismissal covers the press — this covers the
  // case where the flag flips without one reaching us.
  useEffect(() => {
    if (draggingNow) {
      dismissHover();
      if (confirmGesture.current) confirmGesture.current.dragged = true;
    }
  }, [draggingNow, dismissHover]);

  useEffect(() => cancelHoverTimer, [cancelHoverTimer]);

  function handlePointerDown(e: React.PointerEvent) {
    lastPointerType.current = e.pointerType;
    // Preserve the original pointerdown path (including allocations) when
    // confirm-tap is not opted into.
    if (onConfirmTap) {
      confirmGesture.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        dragged: false,
      };
    }
    dismissHover();
  }

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const gesture = confirmGesture.current;
    if (!gesture || gesture.pointerId !== e.pointerId || gesture.dragged) return;
    const dx = e.clientX - gesture.startX;
    const dy = e.clientY - gesture.startY;
    // Match dnd-kit's 8px PointerSensor activation distance. Squared distance
    // avoids a square root on the hottest part of the gesture.
    if (dx * dx + dy * dy >= 64) gesture.dragged = true;
  }, []);

  const handlePointerCancel = useCallback((e: React.PointerEvent) => {
    const gesture = confirmGesture.current;
    if (gesture?.pointerId === e.pointerId) confirmGesture.current = null;
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Keyboard activation has no pointer gesture and must not inherit a stale
    // drag marker from an earlier pointer sequence.
    const wasDrag = e.detail !== 0 && (confirmGesture.current?.dragged ?? false);
    confirmGesture.current = null;
    if (wasDrag) return;
    if (selected && onConfirmTap) {
      onConfirmTap();
      return;
    }
    onClick?.();
  }, [onClick, onConfirmTap, selected]);

  function handleContextMenu(e: React.MouseEvent) {
    if (preview) return;
    e.preventDefault();
    if (lastPointerType.current === 'touch') return;
    dismissHover();
    openDetail(card);
  }

  const ringClass = selected
    ? 'ring-2 ring-sodium'
    : starter
      ? 'ring-1 ring-sodium/40'
      : '';
  // Border color priority: selected > starter > frame variant > default.
  // Frame variant border only applies when neither selected nor starter is set,
  // so player feedback (selection) always wins over decorative framing.
  const borderColor = selected
    ? '#ff8a3d'
    : starter
      ? 'rgba(255,138,61,0.3)'
      : frame.borderColor ?? EDGES.soft;
  const showTripleProgress = copiesOwned === 1 || copiesOwned === 2;

  return (
    <>
    <Tag
      // Callback ref: Tag is a button or a div depending on interactivity, so
      // a typed object ref would have to be one or the other.
      ref={(el: HTMLElement | null) => {
        tileRef.current = el;
      }}
      onClick={isInteractive ? (onConfirmTap ? handleClick : onClick) : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={onConfirmTap ? handlePointerMove : undefined}
      onPointerCancel={onConfirmTap ? handlePointerCancel : undefined}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={dismissHover}
      whileTap={isInteractive ? { scale: 0.97 } : undefined}
      whileHover={isInteractive ? { y: -2 } : undefined}
      transition={{ duration: 0.15 }}
      className={`group relative mx-auto flex flex-col rounded-md border bg-stucco1 text-left transition-colors ${
        // In fill mode the WRAPPER owns the box (height from the rail, width
        // from cardAspectClass) and the card simply fills it.
        fill
          ? 'h-full w-full'
          : `${icon ? 'aspect-square' : 'aspect-[3/4]'} w-full max-w-full ${
              preview ? '' : 'max-h-full'
            }`
      } ${
        isInteractive ? 'hover:bg-stucco2' : ''
      } ${disabled ? 'opacity-40 saturate-50' : ''} ${!affordable ? 'border-dashed' : ''} ${ringClass} ${frame.containerClass}`}
      style={{
        borderColor: affordable ? borderColor : colors.aerosolRed,
        // A held finger on a card is a drag, never a text/image selection —
        // the OS callout would hijack it mid-gesture.
        WebkitTouchCallout: 'none',
        userSelect: 'none',
        // Frame box-shadow only applies when not selected/starter (those use
        // ring-* utilities for emphasis). Avoids visual conflict.
        boxShadow: !selected && !starter ? frame.boxShadow : undefined,
      }}
      aria-pressed={isInteractive ? selected : undefined}
      disabled={isInteractive ? disabled : undefined}
    >
      {/* Portrait area — fills remaining space after foot, so total card
          height is locked to aspect-[3/4] regardless of foot height.
          Background is faction-tinted (placeholder until art) but can be
          overridden by frame variant for mythic/foil cards. */}
      <div
        className={`relative w-full flex-1 overflow-hidden ${icon ? 'rounded-md' : 'rounded-t-md'}`}
        style={{
          // Real art is "grounded": it ships with its own paper and a flat block of the tribe's
          // colour already painted behind the figure (see the `grounded()` helper in
          // tools/portrait_gen_lib.py). It is fully opaque and fills the frame, so the procedural
          // faction gradient underneath it would never be seen — skip it rather than paint a
          // layer that is always covered.
          background: card.art
            ? undefined
            : frame.portraitBackground ?? factionPortraitBackground(card.faction),
        }}
      >
        {/* Tier chip — top-left */}
        <span
          className={`absolute left-[3px] top-[3px] z-10 inline-flex items-center justify-center rounded-full bg-stucco3 font-data text-bone ${
            icon
              ? 'h-3 w-3 text-[7px]'
              : big
                ? 'h-6 w-6 text-[12px]'
                : 'h-4 w-4 text-[10px] sm:h-5 sm:w-5 md:h-6 md:w-6 md:text-[11px]'
          }`}
          aria-hidden
        >
          {card.tier}
        </span>
        {showTripleProgress && (
          <span
            className={`absolute left-1/2 top-[3px] z-10 inline-flex -translate-x-1/2 items-center justify-center rounded-sm border border-sodium/60 bg-asphalt/90 px-[3px] font-data font-bold leading-none text-sodium ${
              icon ? 'h-4 min-w-[24px] text-[10px]' : 'h-5 min-w-[28px] text-[10px] md:text-[11px]'
            }`}
            aria-label={`${copiesOwned} of 3 copies toward a triple`}
            title={`${copiesOwned} of 3 copies toward a triple`}
          >
            {copiesOwned}/3
          </span>
        )}
        {!affordable && (
          <span
            className={`absolute right-[3px] top-[3px] z-10 inline-flex items-center justify-center rounded-sm border border-aerosolRed bg-asphalt/90 px-[2px] font-data font-bold leading-none text-aerosolRed ${
              icon ? 'h-4 text-[10px]' : 'h-5 text-[10px]'
            }`}
            aria-label="Not currently affordable"
            title="Not currently affordable"
          >
            NO $
          </span>
        )}
        {card.art ? (
          /* Real art asset (Nano Banana pipeline, tools/README-image-prompts.md). Opaque and
             generated at 3:4 to match this tile's aspect, so it needs no gradient behind it and
             every procedural identity marker below (tape strip, virgen halo, tribe accessory,
             initials) is suppressed — the drawing carries the identity now.
             Centred rather than top-anchored: these are full-body figures composed on a page with
             deliberate margins, so anchoring to the top would crop the feet off. */
          <img
            src={`${import.meta.env.BASE_URL}${card.art}`}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        ) : (
          <>
            {/* Tape strip — leans across the top, faction-tinted.
                Hidden when an accessory renders OR when the frame variant
                suppresses tribe markers (foils have their own visual register).
                Also hidden in icon mode where the card is too small. */}
            {!showAccessory && frame.showTribeAccessory && !icon && (
              <div
                aria-hidden
                className="absolute -top-[3px] left-[28px] h-[8px] w-[44px] -rotate-3 opacity-70"
                style={{ backgroundColor: tribeColor }}
              />
            )}
            {/* Virgen halo — behind everything, low opacity, centered.
                The vesica piscis sits where the initials are, creating an
                iconographic backdrop that reads as reverence without competing. */}
            {hasVirgenMotif && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center"
              >
                <VirgenMotif
                  size={preview ? 200 : 120}
                  opacity={0.22}
                  className="max-h-[85%] max-w-[85%]"
                />
              </div>
            )}
            {/* Signature accessory — top-right corner of portrait, ~36% width.
                Sits above the radial-gradient background but below the initials
                (z-0 default) so the initials remain the readability anchor when
                the card is small. At preview size, the accessory has room to
                breathe and reads as the dominant identifier. */}
            {showAccessory && visualSpec && Accessory && !icon && (
              <div
                aria-hidden
                className="absolute right-[4px] top-[4px] z-[1] opacity-95"
                style={{ width: preview ? 88 : 48, height: preview ? 88 : 48 }}
              >
                <Accessory
                  bodyColor={visualSpec.paletteHexPrimary}
                  accentColor={visualSpec.paletteHexAccent}
                  size={preview ? 88 : 48}
                  className="h-full w-full drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]"
                />
              </div>
            )}
            {/* Painted initials */}
            <span
              className={`absolute inset-0 flex items-center justify-center leading-none ${
                icon
                  ? 'text-[28px]'
                  : 'text-[40px] sm:text-[52px] md:text-[68px] lg:text-[82px]'
              } ${factionFont}`}
              style={{ color: tribeColor }}
            >
              {initials}
            </span>
          </>
        )}
        {/* Plaque stack counter — Plaqueros snowball resource. Sits under
            the tier chip so it never collides with the accessory. */}
        {plaqueStacks > 0 && !icon && (
          <span
            className="absolute left-[3px] top-[24px] z-10 inline-flex h-4 min-w-[16px] items-center justify-center rounded-sm bg-tecate px-[2px] font-data text-[8px] font-bold text-sharpie sm:top-[28px] sm:h-5 sm:text-[9px] md:top-[32px] md:h-6 md:text-[10px]"
            title={`Plaque tags: ${plaqueStacks}`}
            aria-label={`${plaqueStacks} plaque tags`}
          >
            P{plaqueStacks}
          </span>
        )}
        {/* Stat badges — bottom-left ATK / bottom-right HP. Ring = buffed:
            sodium for permanent above-base stats, ice for pending
            "this combat only" buffs (already folded into the number). */}
        <span
          className={`absolute bottom-[2px] left-[2px] z-10 inline-flex items-center justify-center rounded-sm bg-aerosolRed font-data font-bold text-bone ${
            icon
              ? 'h-[14px] w-[14px] text-[9px]'
              : big
                ? 'h-8 w-8 text-[16px]'
                : 'h-5 w-5 text-[11px] sm:h-6 sm:w-6 sm:text-[13px] md:h-8 md:w-8 md:text-[15px]'
          } ${atkRing}`}
          aria-label={`attack ${displayAtk}`}
          title={tempAtk !== 0 ? 'Buffed this combat' : atkBuffed ? 'Buffed' : undefined}
        >
          {displayAtk}
        </span>
        <span
          className={`absolute bottom-[2px] right-[2px] z-10 inline-flex items-center justify-center rounded-sm bg-glowStick font-data font-bold text-sharpie ${
            icon
              ? 'h-[14px] w-[14px] text-[9px]'
              : big
                ? 'h-8 w-8 text-[16px]'
                : 'h-5 w-5 text-[11px] sm:h-6 sm:w-6 sm:text-[13px] md:h-8 md:w-8 md:text-[15px]'
          } ${hpRing}`}
          aria-label={`health ${displayHp}`}
          title={tempHp !== 0 ? 'Buffed this combat' : hpBuffed ? 'Buffed' : undefined}
        >
          {displayHp}
        </span>
        {starter && (
          <span
            className="absolute bottom-[4px] left-1/2 z-10 -translate-x-1/2 font-data text-[8px] uppercase tracking-[0.2em] text-sodium md:text-[9px]"
            aria-hidden
          >
            starter
          </span>
        )}
      </div>

      {/* Card foot — fixed height so all cards in a row stay uniform.
          Content is justify-center so short cards center vertically and
          long cards still fill the box. Preview mode gets a bigger foot
          and no clipping so all text is fully legible on hover.
          Icon mode drops the foot entirely — the card becomes a square with
          just initials + stats, and the name surfaces by inspecting it. */}
      <div
        className={`shrink-0 flex-col items-center justify-center gap-[1px] overflow-hidden px-xs pb-xs pt-[2px] text-center sm:px-sm sm:pb-sm sm:pt-xs md:px-md md:pb-md ${
          icon ? 'hidden' : 'flex'
        } ${
          preview
            ? 'h-[140px] overflow-visible'
            : big
              ? 'h-[92px]'
              : 'h-[64px] sm:h-[72px] md:h-[82px] lg:h-[68px]'
        }`}
      >
        {/* shrink-0 on the name and tribe: the foot is a fixed-height flex
            column, so without it the rules text (the tallest child) squeezed
            the NAME to zero height — the single most important label on the
            card, gone. Rules text is the one that gives, and it degrades
            gracefully: it is clamped, and the full text is one inspect away. */}
        <span
          className={`shrink-0 leading-tight ${
            preview
              ? 'text-[20px]'
              : big
                ? 'line-clamp-2 text-[15px]'
                : 'line-clamp-2 text-[11px] sm:text-[12px] md:text-[14px] lg:text-[13px]'
          } ${factionFont}`}
          style={{ color: card.faction === 'neutral' ? '#c8b89a' : tribeColor }}
        >
          {card.name}
        </span>
        <span
          className={`shrink-0 font-data uppercase tracking-[0.2em] ${
            preview
              ? 'text-[11px]'
              : big
                ? 'text-[11px]'
                : 'text-[10px] md:text-[11px]'
          }`}
          style={{ color: tribe ? tribeColor : 'transparent' }}
          aria-hidden={!tribe}
        >
          {tribe?.name ?? '—'}
        </span>
        {card.text && (
          <span
            className={`min-h-0 leading-snug text-marker ${
              preview
                ? 'text-[13px]'
                : big
                  ? 'line-clamp-3 text-[12px]'
                  : 'line-clamp-2 text-[10px] md:text-[11px] lg:text-[10px]'
            }`}
          >
            {card.text}
          </span>
        )}
      </div>
    </Tag>
    {hoverActive && hoverAnchor && (
      <CardHoverPreview anchor={hoverAnchor} onDismiss={dismissHover}>
        <CardTile card={card} size="preview" />
      </CardHoverPreview>
    )}
    </>
  );
}
